/* eslint-disable no-unused-vars, no-dupe-keys */
import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import styles from "./insight.module.css";
import { Divider } from "@mui/material";
import InsightTable from "Samarth Module/components/table/insightTable";
import HorizontalBarGraph from "Samarth Module/components/graph/verticalBarGraph";
import StackBarGraph from "Samarth Module/components/graph/stackBarGraph";
import LineGraphOR from "Samarth Module/components/graph/lineGraphOR";
import BarAndLineComboChartProjectedOr from "Samarth Module/components/barAndLineComboChart/barAndLineChartProjectedOr";
import ProjectedORDropdown from "../../../components/dropdown/projectedOrDropdown";
import OEETrendDropdown from "../../../components/dropdown/OEETrendDropdown";
import AlertDemand from "./alertDemand";
import { ALERT_CONFIG, INSIGHT_TEMPLATE } from "./../../../constants/alertInsightConfig";
import BarAndLineComboChartAlternateSite from "Samarth Module/components/barAndLineComboChart/barAndLineChartAlternateSite";
import BarAndLineChartAlternateVendor from "Samarth Module/components/barAndLineComboChart/barAndLineChartAlternateVendor";
import BarAndLineComboChartAlternateExcessInventory from "Samarth Module/components/barAndLineComboChart/barAndLineChartAlternateExcessInventory";
import DownloadIcon from '@mui/icons-material/Download';
import { exportExcelAgileORInsight } from "../../../api/api";
import SnackBarMessage from "../../../components/snackbar/snackbar";
import DeprioritizedCBUCart from '../deprioritizedCBUCart';
import Tooltip from "@mui/material/Tooltip";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AgenticInsights from "Agentic Module/pages/agenticInsights/agenticInsights";

const renderInboundSupplyStatus = (data) => {
  if (!data || (Array.isArray(data) && data.length === 0)) return null;

  return (
    <div>
      {data["Open POs"]?.length > 0 && (
        <>
          <div className={styles.inboundSupplyStatusText}>Open POs</div>
          <InsightTable data={data["Open POs"]} />
        </>
      )}
      {data["Past Due POs"]?.length > 0 && (
        <>
          <div style={{ paddingTop: "7px" }} className={styles.inboundSupplyStatusText}>Past Due POs</div>
          <InsightTable data={data["Past Due POs"]} />
        </>
      )}
    </div>
  );
};

const renderOrTrend = (data) => {
  if (!data?.length) return "";

  if (data[0]?.message) {
    return <InsightTable data={data} />;
  }

  const lineGraphDataOR = [
    {
      name: "Weekly OR",
      data: data[0].WeeklyOR
    },
    {
      name: "Overall Agile OR",
      data: data[1].AgileOR
    }
  ];

  return <LineGraphOR data={lineGraphDataOR} />;
};

const renderOeeTrend = (data, alertDetails) => {
  if (!data?.length) return "";

  const oeeData = data[0];
  const lineGraphDataOEE = [];

  for (const key in oeeData) {
    lineGraphDataOEE.push({
      name: key,
      data: oeeData[key].OEE,
      dataOEEpercent: oeeData[key]["OEE%"],
      "L3M Top 25% OEE": oeeData[key]["L3M Top 25% OEE"],
      "Capacity_master_OEE%": oeeData[key]["Capacity_master_OEE%"]
    });
  }

  if (oeeData.message) {
    return <InsightTable data={data} />;
  }

  return <OEETrendDropdown code={alertDetails["CBU code"]} data={lineGraphDataOEE} />;
};

const renderAlternateSite = (data) => {
  if (data[0]?.message) {
    return <InsightTable data={data} />;
  }

  const graphData = data?.filter(itm => itm?.Type === "Alt site");
  const updateTableData = data?.map(item => {
    const { "Average LT": _, distanceInKM: __, ...rest } = item;

    if (item?.Type === "Alert site") {
      return {
        ...rest,
        "Flex Capacity": 0,
        "Required Capacity": data[0]["Required Capacity"]
      };
    }

    return rest;
  });

  return (
    <>
      <BarAndLineComboChartAlternateSite details={graphData} />
      <InsightTable data={updateTableData?.sort((a, b) => a.Type.localeCompare(b.Type))} />
    </>
  );
};

const getComponentForQuestion = (question, data, alertDetails) => {
  if (data?.message) return <span style={{ fontSize: "12px" }}>{data.message}</span>;

  switch (true) {
    case question.includes("Impacted CBUs"):
      return data?.length ? <HorizontalBarGraph data={data} /> : "";
    case question.includes("Demand supply gap"):
      return (
        <AlertDemand
          details={data["Demand Supply"]}
          baseUnitOfMeasure={alertDetails["Base Unit of Measure"]}
          currentWeek={alertDetails["Week"]}
          isAgileSummary={false}
        />
      );

    case question.includes("Inbound supply status"):
      return renderInboundSupplyStatus(data);

    case question.includes("Alternate vendor status"):

      return data?.length ? (
        <>
          {!data[0].message && <BarAndLineChartAlternateVendor data={data} />}
          <InsightTable data={data} />
        </>
      ) : ""

    case question.includes("Alt site excess inventory"):
      return data?.length ? (
        <>
          {!data[0].message && <BarAndLineComboChartAlternateExcessInventory data={data} Wk={alertDetails["Week"].split(' ')[1]} baseUnitOfMeasure={alertDetails["Base Unit of Measure"]} />}
          <InsightTable data={data} />
        </>
      ) : ""

    case question.includes("Agile OR Summary"):
      return <AlertDemand details={data["Agile OR Summary"]} summaryCapsuleData={data["summary_capsule_data"]} isAgileSummary={true} />;

    case question.includes("WTG Capacity View"):
      return data[0]?.["message"] ? <InsightTable data={data} /> : <StackBarGraph data={data} />;

    case question.includes("OR trend"):
      return renderOrTrend(data);

    case question.includes("OEE Trend"):
      return renderOeeTrend(data, alertDetails);

    case question.includes("Projected OR"):
      return data[0]?.["message"] ? <InsightTable type="Projected OR" data={data} /> : <>
        <ProjectedORDropdown data={data} />
      </>

    case question.includes("Low Priority CBU"):
      return data[0]?.["message"] ? <InsightTable data={data} /> : <DeprioritizedCBUCart data={data} />

    case question.includes("Planned maintenance"):
      return <BarAndLineComboChartProjectedOr plannedData={data["Planned maintenance"]} sharedData={data["Shared maintenance"]} />;

    case question.includes("Alternate site"):
      return renderAlternateSite(data);
    case question.includes("Real time update"):
      return <AgenticInsights alertDetails={alertDetails} />;

    default:
      return data && <InsightTable data={data} />;
  }
};

const Label = ({ label, value, unit }) => {
  const displayValue = unit ? `${value}\u00A0${unit}` : value;

  return (
    <div className={styles.box}>
      <span className={styles.label}>{label}: &nbsp;</span>
      <span className={styles.value}>{displayValue}</span>
    </div>
  );
};

Label.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  unit: PropTypes.string,
};

Label.defaultProps = {
  value: "",
  unit: "",
};

const Insight = ({ data = [], alertDetails, alertType, materialID }) => {
  const [loader, setLoader] = useState(false);
  const [snackBarObject, setSnackBarObject] = useState({
    snackbarOpen: false,
    message: '',
    type: 'error'
  });
  const closeSnackbar = useCallback(() => {
    setSnackBarObject({ message: '', snackbarOpen: false, type: 'error' });
  }, []);
  const config = ALERT_CONFIG[alertType];

  const exportToExcel = async (type) => {
    try {
      setLoader(true);
      await exportExcelAgileORInsight({ type: type });
    } catch (error) {
      setSnackBarObject({ snackbarOpen: true, message: error.message, type: 'error' });
    } finally {
      setLoader(false);
    }
  };


  const getTooltipContent = (question) => {
    const template = INSIGHT_TEMPLATE[question];
    if (template && materialID) {
      return template.replace(/{materialID}/g, materialID);
    }
    return template || `Information about ${question}`;
  };


  return (
    <>
      {data.map((insight) => {
        const isDemandSupplyGap = insight.question === 'Demand supply gap';
        const insightData = data[0]?.data || {};

        const other = insightData?.other;
        const QualityIssue = insightData?.Quality_issue;
        const planVariation = insightData?.plan_variation;
        const supplierIssue = insightData?.supplier_issue;
        const transportIssue = insightData?.Transport_issue;
        const poIssue = insightData?.po_issue;
        const rmpmLedLossAgile = insightData?.summary_capsule_data?.["RMPM Led loss"];
        const machineLedLossAgile = insightData?.summary_capsule_data?.["Machine Led loss"];
        const manpowerShortageAgile = insightData?.summary_capsule_data?.["Manpower shortage"];
        const othersAgile = insightData?.summary_capsule_data?.["Other"];

        return (
          <React.Fragment key={insight.question}>
            {config.showSectionHeaders && insight.question === data[0]?.question && (
              <span style={{ color: 'var(--primary-unilever-color)', fontWeight: 600, fontSize: '16px' }}>
                Alert insights
              </span>
            )}
            <div
              id={insight.question.replace(/\s+/g, '-').toLowerCase()}
              className={styles.demandContainer}
              style={{ scrollMarginTop: "37px" }}
            >
              <div className={styles.demandLabelHeader}>
                <span className={styles.demandHeader} aria-label={insight.question}>
                  {(insight.question === 'Projected OR' && alertDetails.Format) ?
                    `${insight.question}-${alertDetails.Format}`
                    :
                    `${insight.question}`
                  }
                </span>
                {(alertType === 'Agile OR' && insight.question === 'Agile OR Summary') &&
                  <div className={styles.boxContainer}>
                    <Label label="RMPM Led loss" value={rmpmLedLossAgile || 0} unit="%" />
                    <Label label="Machine Led loss" value={machineLedLossAgile || 0} unit="%" />
                    <Label label="Manpower shortage" value={manpowerShortageAgile || 0} unit="%" />
                    <Label label="Other" value={othersAgile || 0} unit="%" />
                  </div>
                }
                {(alertType === 'Agile OR' && insight.question !== 'Agile OR Summary' && !insight.data?.[0]?.message) &&
                  <DownloadIcon
                    color={loader ? "disabled" : "primary"}
                    sx={{
                      cursor: loader ? 'default' : 'pointer',
                      height: '20px',
                      pointerEvents: loader ? 'none' : 'pointer',
                    }}
                    onClick={() => { exportToExcel(insight.question) }}
                  />
                }
                {isDemandSupplyGap && (
                  <div className={styles.boxContainer}>
                    <Label label="Plan variation" value={planVariation} unit="%" />
                    <Label label="Quality Rejected" value={QualityIssue} unit="%" />
                    <Label label="PR to PO conversion" value={poIssue} unit="%" />
                    <Label label="Supplier Delay" value={supplierIssue} unit="%" />
                    <Label label="Transporter Delay" value={transportIssue} unit="%" />
                    <Label label="Other" value={other} unit="%" />
                  </div>
                )}

                {(alertType === 'RMPM Alert') &&
                  <Tooltip
                    title={getTooltipContent(insight.question)}
                    placement="top"
                    componentsProps={{
                      tooltip: {
                        sx: {
                          backgroundColor: "transparent",
                          boxShadow: "none",
                          padding: 0,
                          color: "#2b2b2b",
                          fontSize: "11px",
                          padding: "10px",
                          fontWeight: "500",
                          border: "1px solid lightgray",
                          borderRadius: '3px'
                        },
                      },
                    }}
                  >
                    <InfoOutlinedIcon className={styles.infoIcon} />
                  </Tooltip>
                }
              </div>
              <Divider />
              {getComponentForQuestion(insight.question, insight.data, alertDetails)}
            </div>
            {snackBarObject.snackbarOpen && (
              <SnackBarMessage
                type={snackBarObject.type}
                snackbarMessage={snackBarObject.message}
                snackbarOpen={snackBarObject.snackbarOpen}
                setSnackbarOpen={closeSnackbar}
              />
            )}
            <div>
            </div>
          </React.Fragment>
        );
      })}
    </>
  );
};

Insight.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      question: PropTypes.string,
      data: PropTypes.any,
    })
  ),
  alertDetails: PropTypes.shape({
    Format: PropTypes.string,
  }).isRequired,
  alertType: PropTypes.string.isRequired,
  materialID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

Insight.defaultProps = {
  data: [],
  materialID: "",
};

export default React.memo(Insight);